import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { BrandBanner } from "../components/BrandLogo";
import { Mail, Trash2, ExternalLink } from "lucide-react";

const STATUSES = [
  { value: "new", label: "New", color: "text-[var(--gold-bright)]" },
  { value: "contacted", label: "Contacted", color: "text-[var(--gold-soft)]" },
  { value: "closed", label: "Closed", color: "text-[var(--muted-text)]" },
];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/leads");
      setLeads(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <BrandBanner />
        <div className="flex items-center justify-between mb-6 mt-6">
          <div>
            <h1 className="font-display text-4xl font-bold text-[var(--gold)]">Leads</h1>
            <p className="text-sm text-[var(--muted-text)] mt-1">Prospects who submitted the landing-page contact form.</p>
          </div>
          <span data-testid="leads-count" className="px-4 py-2 rounded-lg border-2 border-[var(--gold)] text-[var(--gold)] text-sm font-bold">{leads.length} total</span>
        </div>

        {loading && <p className="text-[var(--muted-text)]">Loading…</p>}
        {error && <p className="text-red-400">{error}</p>}

        {!loading && leads.length === 0 && (
          <div className="nb-card-static p-10 text-center">
            <Mail className="w-10 h-10 mx-auto text-[var(--gold)]/40 mb-3" />
            <p className="text-[var(--cream)] font-display text-xl">No leads yet.</p>
            <p className="text-[var(--muted-text)] text-sm mt-2">Share the landing-page URL with prospects and they&apos;ll show up here.</p>
          </div>
        )}

        {leads.length > 0 && (
          <div className="grid gap-4">
            {leads.map((l) => (
              <div key={l.id} data-testid={`lead-row-${l.id}`} className="nb-card-static p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-xl font-bold text-[var(--cream)]">{l.name}</h3>
                      <a href={`mailto:${l.email}`} className="text-sm text-[var(--gold)] hover:underline inline-flex items-center gap-1">
                        {l.email} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <p className="text-sm text-[var(--gold-soft)] mt-1">
                      {l.company || "—"} {l.industry && <span className="text-[var(--muted-text)]">· {l.industry}</span>}
                    </p>
                    {l.message && <p className="text-sm text-[var(--cream)]/85 mt-3 leading-relaxed whitespace-pre-wrap">{l.message}</p>}
                    {l.course_code && <p className="text-xs text-[var(--gold-soft)] mt-2 uppercase tracking-wider">Interested in: {l.course_code}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <select
                      data-testid={`lead-status-${l.id}`}
                      value={l.status}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      className="lead-input text-xs w-auto"
                    >
                      {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <span className="text-xs text-[var(--muted-text)]">{new Date(l.created_at).toLocaleString()}</span>
                    <button
                      data-testid={`lead-delete-${l.id}`}
                      onClick={() => removeLead(l.id)}
                      className="text-[var(--muted-text)] hover:text-red-400 transition-colors p-1"
                      title="Delete lead"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
